(ns ui.view.entity-creator.event
  (:require
   [common.localization :as loc]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.controller.websocket.event :as ws]
   [frontend.state.current-session :as session]
   [frontend.view.modals.error-message.view :as error-message]
   [frontend.view.modals.events :as modals]
   [fx.ajax :as ajax]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [ui.cofx.util :as cofx]
   [ui.util :as uu]
   [ui.view.entity-creator.model :as m]
   [ui.view.entity-creator.utils :as u]))

(defn parse-start-value
  [{:keys [entity-type]}
   start-value]
  (let [name-attribute (registry/resolve-name-attribute entity-type)]
    (cond
      (= entity-type :entity.type/person)
      (u/try-parse-contact-info start-value)

      ;; :fund-commitment/name defined by :composite-attributes
      (= entity-type :entity.type/fund-commitment)
      nil

      ;; TODO only for non-calculated fields
      name-attribute
      {(registry/resolve-name-attribute entity-type)
       (cu/str-trim start-value)}

      :else
      nil)))

(defn resolve-initial-edits
  "Returns the map that seeds `entity-edits` at creator init.

   Two modes, distinguished by whether `:entity` is provided:
   - **create** (no `:entity`): runs `enrich-initial-values` to compute the
     per-entity-type create-time defaults (status / pipeline / stage / owner /
     fund / lead-type / lead-source / etc.), then layers parsed `start-value`,
     the auto-`:named-value/attribute` for named-value creators, and any
     map-shaped `start-value`. Caller-passed `:initial-values` are honored as
     the base that `enrich-initial-values` builds on top of.
   - **edit** (`:entity` present): skips ALL prefill — `start-value`,
     `named-value-attribute`, and `enrich-initial-values` defaults are
     create-only signals and would silently shadow real entity values for
     attributes the user hasn't yet touched. Only caller-explicit
     `:initial-values` are returned (as explicit edit-intent)."
  [{:keys [entity entity-type tenant-schema start-value named-value-attribute
           pipeline stages stage raw-config config-path system-mandatory default-account
           initial-values]}]
  (let [editing? (some? entity)
        entity-schema (registry/get-schema tenant-schema entity-type)]
    (if editing?
      (u/ids->option-values {:entity-schema entity-schema}
                            (or initial-values {}))
      (let [entity-config (u/resolve-entity-config
                           {:entity-type entity-type
                            :config-path config-path
                            :raw-config raw-config
                            :stages stages
                            :system-mandatory system-mandatory})
            configured-fields (->> entity-config
                                   :fields
                                   (mapv (fn [{:keys [attribute] :as field-config}]
                                           (let [setting (or (get-in field-config
                                                                     [:stages (:db/id
                                                                               stage)])
                                                             (get-in field-config [:create]))]
                                             [attribute setting])))
                                   (into {}))
            enriched (u/enrich-initial-values
                      {:initial-values initial-values
                       :entity-type entity-type
                       :entity-schema entity-schema
                       :configured-fields configured-fields
                       :stage stage
                       :pipeline pipeline
                       :default-account-id (:db/id default-account)})]
        (merge {}
               ;; if any attribute is search-type
               ;; => replace ids with {:value id :label "LABEL"}s
               (u/ids->option-values {:entity-schema entity-schema} enriched)
               (when (and (string? start-value)
                          (not (cu/blank-value? start-value)))
                 (parse-start-value {:entity-type entity-type} start-value))
               (when (= entity-type :entity.type/named-value)
                 {:named-value/attribute named-value-attribute})
               (when (map? start-value)
                 (u/ids->option-values {:entity-schema entity-schema}
                                       start-value)))))))

(defn build-ident-state
  "Re-frame event-handler that initializes the creator state at `:ident`.

   `event-params` keys read here:
   - `:ident`          — required, where the state lives.
   - `:entity-type`    — required, the entity being created/edited.
   - `:pipeline` / `:stage` — when present, the config is the pipeline's
     `:pipeline/creator-config` rather than the tenant global.
   - `:entity`         — when present, switches `resolve-initial-edits` into
     EDIT mode: no create-time defaults are seeded, only caller-explicit
     `:initial-values` are applied. The original entity itself isn't stored
     in app-db — subscriptions take it via render args.
   - `:initial-values` — caller-explicit attribute → value map, honored in
     both create and edit modes.
   - `:start-value` / `:named-value-attribute` — create-only signals.
   - `:config-path`    — optional override for the creator-config sub-tree.

   Callbacks come from the third positional event arg, NOT from event-params.
   That keeps the modal-lifecycle `:close-modal!` out of any `m/initialize`
   path that shouldn't be allowed to dismiss its parent modal — `initialize`
   forwards only `:on-ok!` / `:on-processed!`, while `pre-open-creator` (used
   by `m/open-creator`, which always opens its own modal) also forwards
   `:close-modal!`."
  [{:keys [db tenant tenant-schema current-account active-user-account-roles stages]}
   [_ {:keys [ident entity entity-type pipeline stage start-value
              named-value-attribute config-path initial-values]} callbacks]]
  (let [global-config (:tenant/entity-creation-config tenant)
        pipeline-config (:pipeline/creator-config pipeline)
        pipeline-config? (and pipeline stage)
        raw-config (if pipeline-config?
                     pipeline-config
                     global-config)
        tenant-modules (:tenant/modules tenant)
        system-mandatory (->> (ecu/mandatory-fields
                               {:tenant tenant
                                :pipeline pipeline
                                :tenant-schema tenant-schema
                                :field-configs (:tenant/field-configurations tenant)
                                :tenant-modules tenant-modules}
                               entity-type)
                              keys
                              set)
        entity-config-path (or config-path
                               (:main-config-path
                                (ecu/resolve-config-paths
                                 {:entity-type entity-type
                                  :pipeline pipeline})))
        initial-edits (resolve-initial-edits
                       {:entity entity
                        :entity-type entity-type
                        :raw-config raw-config
                        :config-path entity-config-path
                        :tenant-schema tenant-schema
                        :start-value start-value
                        :named-value-attribute named-value-attribute
                        :system-mandatory system-mandatory
                        :pipeline pipeline
                        :stages stages
                        :stage stage
                        :initial-values initial-values
                        :default-account (when (contains? active-user-account-roles
                                                          (:db/id current-account))
                                           current-account)})
        ident-state (cond-> {m/entity-type-key entity-type
                             m/callbacks-key callbacks}
                      pipeline
                      (assoc m/pipeline-key pipeline)

                      stage
                      (assoc m/stage-key stage)

                      (seq initial-edits)
                      (assoc-in [m/edits-key m/entity-edits-key]
                                initial-edits))]
    {:db (assoc-in db (m/i-root ident) ident-state)}))

(defn pre-open-creator
  "`m/open-creator` handler. Used by the modal-system bridge which dispatches
   `[m/open-creator props callbacks]` where `callbacks` carries the modal's
   `:close-modal!` (the creator's `m/finish` flow may legitimately call it)."
  [cofx [event-key event-params {:keys [on-ok! on-processed! close-modal!]}]]
  (build-ident-state cofx
                     [event-key event-params
                      {:on-ok! on-ok!
                       :on-processed! on-processed!
                       :close-modal! close-modal!}]))

(defn initialize
  "`m/initialize` handler. Used for in-place / nested creator initialization
   where the parent owns the modal lifecycle. Deliberately does NOT forward a
   `:close-modal!` even if the caller happens to put one in `event-params` —
   the in-place editor must not be able to dismiss its parent modal."
  [cofx [event-key {:keys [on-ok! on-processed!] :as event-params}]]
  (build-ident-state cofx
                     [event-key event-params
                      {:on-ok! on-ok!
                       :on-processed! on-processed!}]))

(rf/reg-event-fx
 m/initialize
 [(rf/inject-cofx cofx/tenant-cofx)
  (rf/inject-cofx cofx/tenant-schema-cofx)
  (rf/inject-cofx cofx/current-account-cofx)
  (rf/inject-cofx m/pipeline-stages-cofx)
  (rf/inject-cofx cofx/active-user-account-roles-cofx)]
 #'initialize)

(defn clear-state
  [{:keys [db]}
   [_ {:keys [ident] :as _event-params}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/i-root ident)))]
    {:db
     new-db}))

(rf/reg-event-fx
 m/clear-state
 []
 #'clear-state)

(rf/reg-event-fx
 m/open-creator
 [(rf/inject-cofx cofx/tenant-cofx)
  (rf/inject-cofx cofx/tenant-schema-cofx)
  (rf/inject-cofx cofx/current-account-cofx)
  (rf/inject-cofx m/pipeline-stages-cofx)
  (rf/inject-cofx cofx/active-user-account-roles-cofx)]
 #'pre-open-creator)

(defn close-creator
  [{:keys [db]}
   [_ {:keys [ident]}]]
  {:db (potpuri/dissoc-in db (m/i-root ident))})

(rf/reg-event-fx
 m/close-creator
 []
 #'close-creator)

(defn contains-path?
  [m path]
  (contains?
   (get-in m (butlast path))
   (last path)))

(defn resolve-current-value
  [{:keys [db entity attribute attribute-path]}]
  (cond
    ;; not edited yet => current value from existing entity
    (not (contains-path? db attribute-path))
    (get entity attribute)

    :else
    (get-in db attribute-path)))

;; update-value:
(defn edit-value
  [{:keys [db]}
   [_ {:keys [ident entity-edits-path attribute new-value conj? entity] :as _args}]]
  (let [edits-full-path (m/edits-full-path ident)
        entity-edits-full-path (into edits-full-path entity-edits-path)
        attribute-path (conj entity-edits-full-path attribute)
        current-value (when conj?
                        (resolve-current-value
                         {:db db
                          :entity entity
                          :attribute attribute
                          :attribute-path attribute-path}))
        ;; ref-multi expects a vector of all values, but for :create-on-fly
        ;; creator's on-ok! only returns the newly created value => explicit conj:
        new-value (if conj?
                    ((fnil conj []) current-value new-value)
                    new-value)
        ;; for future: for existing entity (i.e. editing an existing target-entity)
        ;; 'nil' new-value means clearing/retracting the attribute
        new-db (if (cu/blank-value? new-value)
                 (potpuri/dissoc-in db attribute-path)
                 (assoc-in db attribute-path new-value))]
    {:db new-db}))

(rf/reg-event-fx
 m/edit-value
 ;;[(rf/inject-cofx m/selection-entities-cofx)]
 #'edit-value)

(defn start-create
  "Create the entity. `:enrichment-result-id` in `args`, set when the creator
   reviews an AI enrichment job, creates it through that job."
  [{:keys [db tenant-schema default-currency now]} [_ {:keys [ident] :as args}]]
  (let [entity-type (-> db
                        (get-in (m/entity-type-full-path ident)))
        entity-edits (-> db
                         (get-in (m/entity-edits-full-path ident)))
        entity-schema (registry/get-schema tenant-schema entity-type)
        ;;later (if applicable pipeline-entities): pipeline, selected stage, etc
        ;; (might not be applicable due to new froppo-creation-flow)
        data (u/normalize-entity-edits
              {:entity-schema entity-schema
               :default-currency default-currency}
              (assoc entity-edits :entity/type entity-type))
        new-db (-> db
                   (assoc-in (m/processing-full-path ident) true)
                   (assoc-in (m/processing-start-time-full-path ident) now))]
    (if-let [result-id (:enrichment-result-id args)]
      ;; The creator reviews an AI enrichment job: the entity is created
      ;; through the job, which records it in the same transaction, so a
      ;; reload cannot offer to create it again.
      {:db new-db
       ::ajax/send
       {:operation :ai-enrichment/create-target
        :body      {:result-id result-id :entity data}
        :on-ok     [m/create-success args]
        :on-error  [m/create-error args]}}
      {:db new-db
       ::ws/send
       {:id :entity/create-entity
        :body data
        :on-success [m/create-success args]
        :on-error [m/create-error args]}})))

(rf/reg-event-fx
 m/start-create
 [(rf/inject-cofx cofx/tenant-schema-cofx)
  (rf/inject-cofx cofx/default-currency-cofx)
  (rf/inject-cofx cofx/now-cofx)]
 #'start-create)

(rf/reg-fx
 m/callback-fx
 (fn [callback]
   ;; rf/dispatch-sync not allowed in rf-event-handlers
   ;; => setTimeout as workaround:
   (when (fn? callback)
     (js/setTimeout callback 0))))

(defn finish
  [{:keys [db]} [_ {:keys [ident] :as args}]]
  (let [callbacks (get-in db (m/callbacks-full-path ident))
        {:keys [on-ok! close-modal!]} callbacks
        new-entity (-> db
                       (get-in (m/created-entity-full-path ident)))
        ;; `initialize` deliberately stores no `:close-modal!` - a nested,
        ;; in-place creator must not be able to dismiss the modal its parent
        ;; owns - so it is legitimately nil on that path. Of the call sites
        ;; that invoke the `:close-cb!` they are handed, only a minority check
        ;; it first, and calling nil throws: the callback then aborts partway,
        ;; after it has already navigated or dispatched, leaving the creator
        ;; mounted with its backdrop up. Hand over a no-op instead, so "the
        ;; parent owns the lifecycle" reads to a caller as "closing is not
        ;; yours to do" rather than as a crash. See ai/mistakes.md.
        close-cb! (or close-modal! (constantly nil))
        ok! (if (fn? on-ok!)
              (partial on-ok! {:new-entity-id (:db/id new-entity)
                               :new-entity    new-entity
                               :close-cb!     close-cb!})
              ;; Not `close-cb!`: with no `on-ok!` and no `close-modal!` there
              ;; is genuinely nothing to run, and `callback-fx` already skips a
              ;; non-fn rather than scheduling a pointless no-op.
              close-modal!)]
    {:fx [[:dispatch [m/close-creator args]]
          [m/callback-fx ok!]]}))

(rf/reg-event-fx
 m/finish
 []
 #'finish)

(defn processing-done
  [{:keys [db]} [_ {:keys [ident skip-success-msg?] :as args}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/processing-full-path ident)))]
    {:db new-db
     :fx [(when skip-success-msg?
            [:dispatch [m/finish args]])]}))

(rf/reg-event-fx
 m/processing-done
 []
 #'processing-done)

(defn create-success
  [{:keys [db now]} [_ {:keys [ident] :as args} success-response]]
  (let [callbacks (get-in db (m/callbacks-full-path ident))
        processing-start-time (get-in db (m/processing-start-time-full-path ident))
        {:keys [on-processed!]} callbacks
        new-entity (:body success-response)
        keep-processing? (fn? on-processed!)

        ;; m/processing-done immediately if no on-processed!,
        ;; else on-processed! :close-cb should be called with dispatch m/processing-done
        after-processed! (fn [_]
                           (js/setTimeout
                            (fn []
                              (rf/dispatch [m/processing-done args]))
                            (u/resolve-processing-time processing-start-time now)))
        callback (when keep-processing?
                   (partial on-processed!
                            {:new-entity-id (:db/id new-entity)
                             :new-entity    new-entity
                             ;; A sibling of :body in the response envelope,
                             ;; not part of the entity - the handle for the
                             ;; off-thread search indexing the create kicked
                             ;; off. Forwarded so an :on-processed! that
                             ;; refetches an OpenSearch-backed listing can wait
                             ;; for the index instead of racing it
                             ;; (ui.completion.event).
                             :completion-id (:completion/id success-response)
                             :close-cb!     after-processed!}))
        new-db (-> db
                   (assoc-in (m/created-entity-full-path ident) new-entity))]
    {:db new-db
     :fx [(if-not keep-processing?
            [:dispatch-later
             {:ms (u/resolve-processing-time processing-start-time now)
              :dispatch [m/processing-done args]}]
            [m/callback-fx callback])]}))

(rf/reg-event-fx
 m/create-success
 [(rf/inject-cofx cofx/now-cofx)]
 #'create-success)

(defn open-error-modal!
  [error-message]
  (modals/open-modal!
   error-message/modal-key
   {:title
    (loc/tr :attention)
    :body-content
    error-message
    :ok-button-text
    (loc/tr :ok)}))

(defn create-error
  [{:keys [db]} [_ {:keys [ident] :as _args} error-response]]
  (let [msg (uu/error-response->message error-response)
        new-db (-> db
                   (potpuri/dissoc-in (m/processing-full-path ident)))]
    {:db new-db
     :fx [[::modals/open-modal (partial open-error-modal! msg)]]}))

(rf/reg-event-fx
 m/create-error
 []
 #'create-error)

;; inject stages if pipeline is in event args
(defn pipeline-stages-cofx
  [{:keys [original-event] :as cofx}]
  (let [[_ {:keys [pipeline] :as _e-args}] original-event
        stages (when pipeline
                 (->> (session/pipeline-stages pipeline)
                      (sort-by :common/ord)))]
    (cond-> cofx
      stages(assoc :stages stages))))

(rf/reg-cofx
 m/pipeline-stages-cofx
 #'pipeline-stages-cofx)
